import React, { forwardRef } from "react";
import Select, {
  ActionMeta,
  StylesConfig,
  components,
  GroupBase,
  InputActionMeta,
  MenuListProps,
  MultiValue,
  OptionProps,
  ValueContainerProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowProps,
} from "react-virtualized";
import { CheckIcon, MinusIcon } from "lucide-react";
import { Label } from "../ui/label";
import {
  CustomMultiselectProps,
  SelectableOption,
  SelectAllContextValue,
} from "./type";
import {
  VIRTUALIZED_MENU_MAX_HEIGHT,
  VIRTUALIZED_MENU_MIN_ROW_HEIGHT,
  VIRTUALIZED_MENU_OVERSCAN,
} from "@/shared/constants/virtualizedDefaultValues";

export const MaxValueContainer = <Option, IsMulti extends boolean>(
  props: ValueContainerProps<Option, IsMulti>,
): React.ReactElement => {
  const selectedValues = props.getValue();

  if (selectedValues.length > 1) {
    return (
      <components.ValueContainer {...props}>
        {selectedValues.length} selected
      </components.ValueContainer>
    );
  }

  return <components.ValueContainer {...props} />;
};

const VirtualizedMenuList = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
): React.ReactElement => {
  const { children, maxHeight, getValue, innerProps, innerRef, options } =
    props;
  const childArray = React.Children.toArray(children);
  const selectedValue = getValue()[0];
  const selectedIndex =
    selectedValue !== undefined
      ? options.findIndex((option) => option === selectedValue)
      : -1;
  const listHeight = Math.min(maxHeight, VIRTUALIZED_MENU_MAX_HEIGHT);
  const measurementCache = React.useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: VIRTUALIZED_MENU_MIN_ROW_HEIGHT,
        minHeight: VIRTUALIZED_MENU_MIN_ROW_HEIGHT,
      }),
    [],
  );

  React.useEffect(() => {
    measurementCache.clearAll();
  }, [childArray.length, measurementCache, options]);

  if (childArray.length === 0 || childArray.length !== options.length) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  const rowRenderer = ({
    index,
    key,
    parent,
    style,
  }: ListRowProps): React.ReactElement => (
    <CellMeasurer
      cache={measurementCache}
      columnIndex={0}
      key={key}
      parent={parent}
      rowIndex={index}
    >
      <div style={style}>{childArray[index] as React.ReactElement}</div>
    </CellMeasurer>
  );

  return (
    <div
      {...innerProps}
      ref={innerRef}
      style={{
        maxHeight: listHeight,
        overflow: "hidden",
      }}
    >
      <div style={{ height: listHeight }}>
        <AutoSizer disableHeight>
          {({ width }): React.ReactElement => (
            <List
              deferredMeasurementCache={measurementCache}
              height={listHeight}
              overscanRowCount={VIRTUALIZED_MENU_OVERSCAN}
              rowCount={childArray.length}
              rowHeight={measurementCache.rowHeight}
              rowRenderer={rowRenderer}
              scrollToIndex={selectedIndex >= 0 ? selectedIndex : undefined}
              width={Math.max(width, 0)}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

const getOptionKey = <Option, >(option: Option): string => {
  const selectableOption = option as SelectableOption;

  if (selectableOption.value !== undefined) {
    return String(selectableOption.value);
  }

  return JSON.stringify(option);
};

const getOptionLabel = <Option, >(option: Option): string => {
  const selectableOption = option as SelectableOption;

  if (selectableOption.label !== undefined) {
    return selectableOption.label;
  }

  if (selectableOption.value !== undefined) {
    return String(selectableOption.value);
  }

  return "";
};

const isOptionDisabled = <Option, >(option: Option): boolean => {
  const selectableOption = option as SelectableOption;

  return selectableOption.isDisabled === true;
};

const filterOptionsByInput = <Option, >(
  options: Option[],
  inputValue: string,
): Option[] => {
  const normalizedInput = inputValue.trim().toLowerCase();

  if (!normalizedInput) {
    return options;
  }

  return options.filter((option) => {
    const selectableOption = option as SelectableOption;
    const searchableText = [
      getOptionLabel(option),
      selectableOption.value !== undefined
        ? String(selectableOption.value)
        : "",
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedInput);
  });
};

/**
 * Visual-only checkbox used inside react-select dropdown options.
 * Uses CheckboxPrimitive.Root appearance but is not interactive — avoids
 * the Radix internal synthetic click (from its hidden <input>) that
 * bubbles into react-select and causes an infinite update loop.
 * Selection is handled by react-select's Option component instead.
 */
const VisualCheckbox = React.memo(
  ({ checked }: { checked: boolean | "indeterminate" }): React.ReactElement => {
    const isIndeterminate = checked === "indeterminate";
    const isChecked = checked === true;
    return (
      <span
        aria-hidden="true"
        data-state={
          isIndeterminate
            ? "indeterminate"
            : isChecked
              ? "checked"
              : "unchecked"
        }
        className="peer border-gray-400 text-white data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=checked]:border-primary data-[state=indeterminate]:border-primary size-5 shrink-0 rounded-6 border inline-grid place-content-center pointer-events-none"
      >
        {isIndeterminate && <MinusIcon className="size-3.5" />}
        {isChecked && <CheckIcon className="size-3.5" />}
      </span>
    );
  },
);
VisualCheckbox.displayName = "VisualCheckbox";

const CheckboxOption = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group>,
): React.ReactElement => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <VisualCheckbox checked={props.isSelected} />
      <span>{props.label}</span>
    </div>
  </components.Option>
);

const SelectAllContext = React.createContext<SelectAllContextValue | null>(
  null,
);

const SelectAllMenuList = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  menuListProps: MenuListProps<Option, IsMulti, Group>,
): React.ReactElement => {
  const selectAllContext = React.useContext(SelectAllContext);

  if (!selectAllContext) {
    return <components.MenuList {...menuListProps} />;
  }

  const {
    handleSelectAll,
    isSelectAllChecked,
    isSelectAllIndeterminate,
    relevantOptionsLength,
    selectAllLabel,
  } = selectAllContext;

  return (
    <components.MenuList {...menuListProps}>
      <button
        aria-checked={
          isSelectAllIndeterminate
            ? "mixed"
            : isSelectAllChecked
              ? "true"
              : "false"
        }
        role="checkbox"
        className="flex w-full items-center gap-2 border-b border-border bg-[var(--white)] px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:opacity-60"
        disabled={relevantOptionsLength === 0}
        onClick={(event): void => {
          if (event.detail === 0) {
            handleSelectAll();
          }
        }}
        onMouseDown={(event): void => {
          event.preventDefault();
          event.stopPropagation();
          handleSelectAll();
        }}
        type="button"
      >
        <VisualCheckbox
          checked={
            isSelectAllIndeterminate ? "indeterminate" : isSelectAllChecked
          }
        />
        <span>{selectAllLabel}</span>
      </button>
      {menuListProps.children}
    </components.MenuList>
  );
};

const CustomMultiselectInner = <Option, IsMulti extends boolean = false>(
  props: CustomMultiselectProps<Option, IsMulti>,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement => {
  const {
    options,
    value,
    onChange,
    onCreateOption,
    placeholder,
    className,
    labelName,
    wrapperClassName,
    required,
    isMulti,
    isDisabled,
    isCreatable,
    errors,
    handleKeyDown,
    components: selectComponents,
    menuPortalTarget,
    menuPosition,
    enableVirtualization = false,
    showSelectAll = true,
    selectAllLabel = "Select all",
  } = props;
  const [inputValue, setInputValue] = React.useState("");

  const Component = isCreatable ? CreatableSelect : Select;
  const canSelectAll = Boolean(isMulti && showSelectAll);
  const availableOptions = React.useMemo(
    () => options?.filter((option) => !isOptionDisabled(option)) ?? [],
    [options],
  );
  const relevantOptions = React.useMemo(
    () => filterOptionsByInput(availableOptions, inputValue),
    [availableOptions, inputValue],
  );
  const selectedOptions = React.useMemo<MultiValue<Option>>(
    () => (Array.isArray(value) ? (value as MultiValue<Option>) : []),
    [value],
  );
  const selectedOptionKeys = React.useMemo(
    () => new Set(selectedOptions.map((option) => getOptionKey(option))),
    [selectedOptions],
  );
  const relevantOptionKeys = React.useMemo(
    () => new Set(relevantOptions.map((option) => getOptionKey(option))),
    [relevantOptions],
  );
  const selectedRelevantCount = relevantOptions.filter((option) =>
    selectedOptionKeys.has(getOptionKey(option)),
  ).length;
  const isSelectAllChecked =
    relevantOptions.length > 0 &&
    selectedRelevantCount === relevantOptions.length;
  const isSelectAllIndeterminate =
    selectedRelevantCount > 0 && selectedRelevantCount < relevantOptions.length;

  const handleSelectAll = React.useCallback((): void => {
    if (!canSelectAll || !onChange || relevantOptions.length === 0) {
      return;
    }

    const nextValue: MultiValue<Option> = isSelectAllChecked
      ? selectedOptions.filter(
          (option) => !relevantOptionKeys.has(getOptionKey(option)),
        )
      : [
          ...selectedOptions,
          ...relevantOptions.filter(
            (option) => !selectedOptionKeys.has(getOptionKey(option)),
          ),
        ];

    onChange(
      nextValue as unknown as IsMulti extends true ? MultiValue<Option> : never,
      {
        action: isSelectAllChecked ? "deselect-option" : "select-option",
      } as ActionMeta<Option>,
    );
  }, [
    canSelectAll,
    isSelectAllChecked,
    onChange,
    relevantOptionKeys,
    relevantOptions,
    selectedOptionKeys,
    selectedOptions,
  ]);

  const handleInputChange = React.useCallback(
    (newValue: string, actionMeta: InputActionMeta): string => {
      if (actionMeta.action === "input-change") {
        setInputValue(newValue);
      }

      if (actionMeta.action === "set-value") {
        setInputValue("");
      }

      if (actionMeta.action === "menu-close") {
        setInputValue("");
      }

      return newValue;
    },
    [],
  );

  const selectAllContextValue = React.useMemo<SelectAllContextValue>(
    () => ({
      handleSelectAll,
      isSelectAllChecked,
      isSelectAllIndeterminate,
      relevantOptionsLength: relevantOptions.length,
      selectAllLabel,
    }),
    [
      handleSelectAll,
      isSelectAllChecked,
      isSelectAllIndeterminate,
      relevantOptions.length,
      selectAllLabel,
    ],
  );

  const customStyles: StylesConfig<Option, IsMulti> = {
    option: (provided, state) => {
      const data = state.data as {
        isActive?: boolean;
        isDisabled?: boolean;
      };

      const isInactiveOption = data.isActive === false;
      const isDisabledOption = data.isDisabled === true;
      const optionBackgroundColor =
        state.isFocused || state.isSelected
          ? "var(--bg-hover)"
          : "var(--white)";

      return {
        ...provided,
        backgroundColor:
          isInactiveOption && state.isFocused
            ? "transparent"
            : optionBackgroundColor,
        color: "var(--foreground)",

        // Inactive option (like .inactive-option)
        borderLeft: isInactiveOption
          ? "3px solid var(--color-destructive)"
          : undefined,

        //  Disabled option (like .disable-option)
        opacity: isDisabledOption ? 0.6 : provided.opacity,
        pointerEvents: isDisabledOption ? "none" : provided.pointerEvents,
        cursor: isDisabledOption ? "not-allowed" : provided.cursor,
        border: isDisabledOption ? "0" : provided.border,
      };
    },
    menuPortal: (base) => ({
      ...base,
      zIndex: 999,
      pointerEvents: "auto",
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: 200,
      overflowY: enableVirtualization ? "hidden" : "auto",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
  };

  const mergedComponents = React.useMemo(
    () => ({
      ...(enableVirtualization && !canSelectAll
        ? {
            MenuList: VirtualizedMenuList,
          }
        : {}),
      ...(canSelectAll
        ? {
            Option: CheckboxOption,
            MenuList: SelectAllMenuList,
          }
        : {}),
      ...selectComponents,
    }),
    [canSelectAll, enableVirtualization, selectComponents],
  );

  return (
    <div
      ref={ref}
      className={`custom-multiselect-wrapper ${wrapperClassName ?? ""}`}
    >
      {labelName && (
        <Label className="mb-1.5">
          {labelName}
          {required && <span className="text-destructive -ml-1">*</span>}
        </Label>
      )}

      <SelectAllContext.Provider value={selectAllContextValue}>
        <Component<Option, IsMulti>
          isMulti={isMulti}
          isDisabled={isDisabled}
          options={options}
          value={value}
          onChange={onChange}
          {...(isCreatable && onCreateOption ? { onCreateOption } : undefined)}
          placeholder={placeholder}
          className={`custom-multiselect ${className ?? ""}`}
          classNamePrefix="custom-multiselect"
          onKeyDown={handleKeyDown}
          onInputChange={handleInputChange}
          styles={customStyles}
          isOptionDisabled={isOptionDisabled}
          components={mergedComponents}
          closeMenuOnSelect={canSelectAll ? false : undefined}
          hideSelectedOptions={canSelectAll ? false : undefined}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          closeMenuOnScroll={false} // Prevents menu from closing when parent scrolls
          captureMenuScroll={false} // Stops React Select from hijacking the scroll event
          menuShouldScrollIntoView={true}
        />
      </SelectAllContext.Provider>
      {errors && <p className="mt-1 text-sm text-red-500">{errors}</p>}
    </div>
  );
};

const CustomMultiselect = forwardRef(CustomMultiselectInner) as <
  Option,
  IsMulti extends boolean = false,
>(
  props: CustomMultiselectProps<Option, IsMulti> & {
    ref?: React.Ref<HTMLDivElement>;
  },
) => React.ReactElement;

export default CustomMultiselect;
